import { Core, WebViewerInstance } from '@pdftron/webviewer';
import { useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { ValueOf } from 'type-fest';

import { suggestionAnnotations } from '../data/actions';
import type { SuggestionAnnotationClassType } from './SuggestionAnnotation';
import {
  NAMESPACE,
  ANNOTATION_SELECTION_TYPES,
  SUGGESTION_ANNOTATION_COMMAND_TYPES,
  SUGGESTION_ANNOTATION_COMMAND_ORIGINS,
} from './consts';

export const useAnnotationOnSelectCallbacks = ({
  instance,
  annotationManager,
  SuggestionAnnotationClass,
}: {
  instance: WebViewerInstance | null;
  annotationManager: Core.AnnotationManager | null;
  SuggestionAnnotationClass: SuggestionAnnotationClassType | null;
}) => {
  const dispatch = useDispatch();
  // NOTE: maintain this so we can turn off the delete action in the annotation popup for just our
  //  custom annotations
  const originalAnnotationPopupItemsRef = useRef<object[] | null>(null);

  useEffect(() => {
    if (instance && annotationManager && SuggestionAnnotationClass) {
      // Save the original annotation popup items once
      if (!originalAnnotationPopupItemsRef.current) {
        originalAnnotationPopupItemsRef.current =
          instance.UI.annotationPopup.getItems();
      }

      // manage event listener for the deselection event - we want to debounce
      //  actually dispatching this because this is fired right before selecting
      //  the same annotation if it is already selected
      annotationManager.addEventListener(
        `annotationDeselected.${NAMESPACE}`,
        (annotationsList: Core.Annotations.Annotation[]) => {
          const annots = annotationsList.filter(
            (annot) => annot instanceof SuggestionAnnotationClass,
          );

          if (annots.length > 0) {
            // Restore original annotation popup items when SuggestionAnnotation is deselected
            if (originalAnnotationPopupItemsRef.current) {
              instance.UI.annotationPopup.update(
                originalAnnotationPopupItemsRef.current,
              );
            }

            dispatch(
              suggestionAnnotations.queueCommands({
                commands: [
                  {
                    type: SUGGESTION_ANNOTATION_COMMAND_TYPES.UNFOCUS,
                    origin: SUGGESTION_ANNOTATION_COMMAND_ORIGINS.VIEWER,
                    annotationIds: annots.map((annot) => annot.Id),
                  },
                ],
              }),
            );
          }
        },
      );

      // manage event listener for the selection on event
      annotationManager.addEventListener(
        `annotationSelected.${NAMESPACE}`,
        (
          annotationsList: Core.Annotations.Annotation[],
          selectionType: ValueOf<typeof ANNOTATION_SELECTION_TYPES>,
        ) => {
          if (selectionType === ANNOTATION_SELECTION_TYPES.DESELECTED) {
            // Apryse prefers handling deselected explicitly with deselected event
            //  https://docs.apryse.com/documentation/web/guides/annotation/annotationmanager/events/#annotationselected
            return;
          }

          const annots = annotationsList.filter(
            (annot) => annot instanceof SuggestionAnnotationClass,
          );
          if (annots.length > 0) {
            // Remove delete-related items from annotation popup when SuggestionAnnotation is selected
            if (originalAnnotationPopupItemsRef.current) {
              const deleteRelatedDataElements = [
                'annotationDeleteButton',
                'annotationGroupButton',
              ];

              const filteredItems =
                originalAnnotationPopupItemsRef.current.filter(
                  (item: object) =>
                    !deleteRelatedDataElements.includes(
                      (item as { dataElement?: string }).dataElement ?? '',
                    ),
                );

              // NOTE: this update causes a blur event on surrounding elements, which can be
              //  problematic for popovers and selects - ideally we find a better way to
              //  customize the popup menu to not have delete actions for only custom annotation
              //  but this is the best i could figure out for now.
              //  We solved this problem by with useOutsideClick in the popover component.
              instance.UI.annotationPopup.update(filteredItems);
            }

            dispatch(
              suggestionAnnotations.queueCommands({
                commands: [
                  {
                    type: SUGGESTION_ANNOTATION_COMMAND_TYPES.FOCUS,
                    origin: SUGGESTION_ANNOTATION_COMMAND_ORIGINS.VIEWER,
                    annotationIds: annots.map((annot) => annot.Id),
                  },
                ],
              }),
            );
          }
        },
      );
    }

    return () => {
      if (annotationManager) {
        // kill all event listeners in namespace
        annotationManager.removeEventListener(
          `annotationSelected.${NAMESPACE}`,
        );
        annotationManager.removeEventListener(
          `annotationDeselected.${NAMESPACE}`,
        );
      }

      // Restore original popup items on cleanup
      if (instance && originalAnnotationPopupItemsRef.current) {
        instance.UI.annotationPopup.update(
          originalAnnotationPopupItemsRef.current,
        );
      }
    };
  }, [instance, annotationManager, SuggestionAnnotationClass, dispatch]);
};
